"use client";

import type { ReactNode } from "react";
import { cx } from "../fortschritt/breite";
import { Knopf } from "../knopf";

/**
 * Werkzeugleiste (schwebende Leiste unten auf einer Arbeitsfläche, V6 Z4; Design-Guide `werkzeug-leiste.md`, Betreiber 01.11.2026 „Empfehlung,
 * 1 nur Zurück, 2 ja, 3 ja“): Herkunft ist die Pro-Leiste `ui-components/ui-components-cat-editor-and-toolbar-menus/floating-canvas-toolbar`
 * (Symbolknöpfe in einer abgerundeten Leiste, Trenner zwischen den Gruppen). Der Baustein ist allgemein: er zeigt Gruppen von Werkzeugen
 * und meldet die Wahl; er kennt weder Zeichenfläche noch Rechte. Wahlwerkzeuge (Auswählen, Rechteck) tragen `aria-pressed`, Aktionen
 * (Zoom) nicht. Jeder Knopf hat Beschriftung (`aria-label`, Tooltip über `title`) und ein Kurzwort für die Tastatur im Label, wo es eines gibt.
 * Ein gesperrtes Werkzeug trägt den Grund (`grund`) als Text direkt neben der Leiste (r262, nie nur grau): der Aufrufer setzt ihn als `hinweis`.
 * Kein Recht, kein Zustand: der Aufrufer reicht nur die Werkzeuge, die die Person nutzen darf.
 */
export interface WerkzeugEintrag {
  id: string;
  label: string;
  /** Symbol (Lucide, vom Aufrufer; Größe `size-4`). */
  symbol: ReactNode;
  /** Wahlwerkzeug: gedrückt, solange es das aktive ist. Fehlt die Angabe, ist es eine Aktion ohne Dauerzustand. */
  gewaehlt?: boolean;
  deaktiviert?: boolean;
}

export interface WerkzeugGruppe {
  id: string;
  /** Name der Gruppe für Hilfstechnik („Zeichenwerkzeuge“). */
  label: string;
  eintraege: readonly WerkzeugEintrag[];
}

const LEISTE = "inline-flex flex-wrap items-center gap-1 p-1 bg-layer border border-layer-line rounded-xl shadow-lg";

export function WerkzeugLeiste({
  label,
  gruppen,
  beiWahl,
  hinweis,
  mitte = true,
}: {
  /** Name der Leiste für Hilfstechnik („Werkzeuge der Markierfläche“). */
  label: string;
  gruppen: readonly WerkzeugGruppe[];
  beiWahl: (id: string) => void;
  /** Satz unter der Leiste (z. B. Grund eines gesperrten Werkzeugs, Kurzhilfe der Tastatur). */
  hinweis?: ReactNode;
  /** Leiste mittig unter der Fläche (Vorgabe) statt linksbündig. */
  mitte?: boolean;
}) {
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-editor-and-toolbar-menus/floating-canvas-toolbar */}
      <div className={cx("flex flex-col gap-2", mitte ? "items-center" : "items-start")} data-werkzeug-leiste="">
        <div role="toolbar" aria-label={label} className={LEISTE}>
          {gruppen.map((g, i) => (
            <div key={g.id} role="group" aria-label={g.label} className="flex items-center gap-1">
              {i > 0 && <span className="mx-1 h-6 w-px bg-line-3" aria-hidden="true" />}
              {g.eintraege.map((e) => (
                <Knopf
                  key={e.id}
                  nurSymbol
                  label={e.label}
                  symbol={e.symbol}
                  variante={e.gewaehlt ? "primaer" : "ghost"}
                  groesse="md"
                  ariaPressed={e.gewaehlt === undefined ? undefined : e.gewaehlt}
                  disabled={e.deaktiviert}
                  onClick={() => beiWahl(e.id)}
                  daten={{ "data-werkzeug": e.id }}
                />
              ))}
            </div>
          ))}
        </div>
        {hinweis && <p className="text-xs text-muted-foreground-1 text-center">{hinweis}</p>}
      </div>
    </>
  );
}
